import AddIcon from '@material-ui/icons/Add';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { ReactElement, useState } from 'react';

import { isoToLocal } from '~/utils/date/date';

import Button from '~/ui/components/common/Button';
import ListItem from '~/ui/components/common/ListItem';

import { IClientTeam } from '~/services/api/clientDetails/types';
import { ReactComponent as TeamLeaderIcon } from '~/ui/assets/images/teamLeader.svg';

import styles from './TeamsDetailsBlock.module.scss';

interface IProps {
  title: string;
  buttonText?: string;
  showEdit?: boolean;
  active: IClientTeam[];
  archived: IClientTeam[];
  noActions?: boolean;
  showArchive?: boolean;
  showRestore?: boolean;
  showArchivedButton?: boolean;
  labelGrey?: boolean;
  onAdd?: () => void;
  onEdit?: (num: number, name: string) => void;
  setDetailsInfo?: (data: any) => void;
}

const TeamsDetailsBlock = ({
  title,
  active,
  archived,
  buttonText,
  showEdit = true,
  noActions,
  showArchive = true,
  showRestore = true,
  showArchivedButton,
  labelGrey,
  onAdd,
  onEdit,
  setDetailsInfo,
}: IProps): ReactElement => {
  const [showArchivedList, setShowArchivedList] = useState(false);

  const toggle = () => setShowArchivedList(val => !val);

  const icon = !showArchivedList ? (
    <ExpandMoreIcon color="primary" />
  ) : (
    <ExpandLessIcon color="primary" />
  );

  const extractTeamAdmissionInfo = ({ admissionDetails }: IClientTeam) => [
    `Referral Source: ${admissionDetails.referralSource?.name}`,
    `Date of Admission: ${isoToLocal(admissionDetails.admissionDate)}`,
  ];

  const CenterContent = ({ rows }: { rows: string[] }) => (
    <div className={styles.descriptionWrapper}>
      {rows.map(row => (
        <div key={row} className={styles.grey}>
          {row}
        </div>
      ))}
    </div>
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.topBlock}>
        <h4 className={styles.title}>{title}</h4>
        {buttonText && (
          <Button startIcon={<AddIcon />} variant="outlined" color="primary" onClick={onAdd}>
            {buttonText}
          </Button>
        )}
      </div>
      {!!active.length &&
        active.map((item, idx) => (
          <ListItem
            noActions={noActions}
            key={item.id}
            label={item.team?.name}
            content={<CenterContent rows={extractTeamAdmissionInfo(item)} />}
            className={
              (active.length === 1 || active.length - 1 === idx) && !!archived.length
                ? styles.noBorder
                : ''
            }
            onEdit={() => onEdit(item.id, item.team?.name)}
            showEdit={showEdit}
            showArchived={showArchive}
            disableArchived={item.isPrimary && active.length > 1}
            startIcon={item.isPrimary ? <span>Primary</span> : undefined}
            onArchive={() =>
              setDetailsInfo({ method: 'archive', id: item.id, name: item.team?.name })
            }
          />
        ))}
      {showArchivedButton && !!archived.length && (
        <div className={styles.archivedButtonWrapper}>
          <button type="button" onClick={toggle} className={styles.archivedButton}>
            Archived {icon}
          </button>
        </div>
      )}

      {!!archived.length &&
        showArchivedList &&
        archived.map(item => {
          const itemLabel = item.team?.name;
          return (
            <ListItem
              noActions={noActions}
              key={item.id}
              label={itemLabel}
              content={<CenterContent rows={extractTeamAdmissionInfo(item)} />}
              onEdit={() => onEdit(item.id, itemLabel)}
              showRestore={showRestore}
              labelGrey={labelGrey}
              startIcon={item.isPrimary ? <TeamLeaderIcon /> : undefined}
              onRestore={() => setDetailsInfo({ method: 'restore', id: item.id, name: itemLabel })}
            />
          );
        })}
    </div>
  );
};

export default TeamsDetailsBlock;
